import { useState } from "react";
import { ArrowDownUp, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getTaskPriorityMeta,
  TASK_PRIORITY_OPTIONS,
  type TaskPriority,
} from "@/lib/task-priority";
import { cn } from "@/lib/utils";

interface TaskPriorityFilterButtonProps {
  selectedPriorities: TaskPriority[];
  onChange: (priorities: TaskPriority[]) => void;
}

export function TaskPriorityFilterButton({
  selectedPriorities,
  onChange,
}: TaskPriorityFilterButtonProps) {
  const [open, setOpen] = useState(false);
  const hasFilter = selectedPriorities.length > 0;

  const togglePriority = (priority: TaskPriority) => {
    if (selectedPriorities.includes(priority)) {
      onChange(selectedPriorities.filter((value) => value !== priority));
    } else {
      onChange([...selectedPriorities, priority]);
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant={hasFilter ? "default" : "outline"}
          size="sm"
          className="shrink-0 relative h-9 w-9 md:h-8 md:w-8"
          title="Filtrar por prioridade"
        >
          <ArrowDownUp />
          {hasFilter && (
            <span className="absolute -top-1 -right-1 min-w-4 h-4 rounded-full bg-primary px-1 text-[9px] font-bold leading-4 text-primary-foreground">
              {selectedPriorities.length}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Filtrar por prioridade
        </DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={(event) => {
            event.preventDefault();
            onChange([]);
          }}
          className="flex items-center justify-between"
        >
          <span>Todas</span>
          {!hasFilter && <CheckCircle className="w-4 h-4 text-primary" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {TASK_PRIORITY_OPTIONS.map((option) => {
          const isSelected = selectedPriorities.includes(option.value);
          const meta = getTaskPriorityMeta(option.value);
          return (
            <DropdownMenuItem
              key={option.value}
              onSelect={(event) => {
                event.preventDefault();
                togglePriority(option.value);
              }}
              className="flex items-center gap-2"
            >
              <Badge
                variant="outline"
                className={cn("text-[10px] px-1.5 py-0 h-5", meta.className)}
              >
                {meta.label}
              </Badge>
              <span className="flex-1" />
              {isSelected && <CheckCircle className="w-4 h-4 text-primary" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
